import { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

import { CommonResponse } from 'src/types';
import { refreshTokens } from 'src/api/auth';
import { getRefreshToken, setTokens, clearTokens } from 'src/utils/token-storage';
import { ENDPOINTS } from 'src/utils/axios';

// ----------------------------------------------------------------------

let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

export async function performTokenRefresh(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  if (!isRefreshing && !refreshPromise) {
    isRefreshing = true;
    refreshPromise = refreshTokens({
      refresh_token: refreshToken,
      platform_id: new URLSearchParams(window.location.search).get('platform_id') || '',
    })
      .then((tokens) => {
        setTokens(tokens.access_token, tokens.refresh_token);
        return tokens.access_token;
      })
      .catch(() => {
        clearTokens();
        return null;
      })
      .finally(() => {
        isRefreshing = false;
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

// ----------------------------------------------------------------------

export async function handle401Error(
  error: AxiosError<CommonResponse<null>>,
  axiosInstance: AxiosInstance
) {
  const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

  if (
    error.response?.status !== 401 ||
    !originalRequest ||
    originalRequest._retry ||
    originalRequest.url === ENDPOINTS.auth.refresh
  ) {
    return null;
  }

  originalRequest._retry = true;

  const newToken = await performTokenRefresh();

  if (!newToken) return null;

  originalRequest.headers.Authorization = `Bearer ${newToken}`;
  return axiosInstance(originalRequest);
}
